// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Every screen that is words rather than a board. Menus, the help card, About, and the results — and
// the results are the one that matters, because it is the only moment two players compare what they
// actually did.

import { escapeHtml } from './dom';
import { MODES, MODE_IDS, type Mode } from './modes';
import { TIERS, type TierId } from './bot';
import { SEAT_NAME } from './palette';
import { STANCES, UNITS, WINDOW_S } from './tuning';
import type { MatchState } from './rules';

export const ATTRIB =
  'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · ' +
  '<a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more games, tools &amp; sites</a>';

export function menuHtml(mode: Mode, tier: TierId, best: number): string {
  return `
    <section class="screen menu">
      <h1 class="title">Standoff</h1>
      <p class="tagline">You both plan the same five seconds. Then you watch them happen at once.</p>

      <div class="picker" role="radiogroup" aria-label="Mode">
        ${MODE_IDS.map(
          (id) => `<button class="pick ${id === mode.id ? 'on' : ''}" data-mode="${id}" role="radio"
            aria-checked="${id === mode.id}"><b>${MODES[id].name}</b><span>${escapeHtml(MODES[id].blurb)}</span></button>`,
        ).join('')}
      </div>
      <p class="rule">${escapeHtml(mode.rule)}</p>

      <div class="picker tiers" role="radiogroup" aria-label="Opponent">
        ${TIERS.map(
          (t) => `<button class="pick small ${t.id === tier ? 'on' : ''}" data-tier="${t.id}" role="radio"
            aria-checked="${t.id === tier}"><b>${t.name}</b><span>${escapeHtml(t.blurb)}</span></button>`,
        ).join('')}
      </div>

      <button class="primary" id="play">Play</button>
      ${best > 0 ? `<p class="best">Best: ${best} ${best === 1 ? 'win' : 'wins'} in ${MODES[mode.id].name}</p>` : ''}
      <div class="menu-links">
        <button class="link" id="how">How to play</button>
        <button class="link" id="rules">The rules</button>
        <button class="link" id="about">About</button>
        <button class="link" id="mute" aria-pressed="false">Sound on</button>
      </div>
    </section>`;
}

export function helpHtml(): string {
  return `
    <div class="panel-body">
      <p><b>Both of you write orders for the same five seconds, at the same time, in secret.</b>
      Then both plans run at once and you watch what happened.</p>
      <ol class="steps">
        <li><b>Tap a unit</b>, then <b>drag on the board</b> to draw where it walks. The dots on the
        route are seconds — that is when it arrives.</li>
        <li><b>Drag the arrowhead</b> to choose which way it watches. A unit only shoots inside the
        wedge it is watching, so this is the guess you are really making.</li>
        <li><b>Stalk / Move / Rush</b> trades ground for readiness. Rushing cannot shoot at all.</li>
        <li><b>Hold the mast</b> — you bank time only while your side is in the field and theirs is
        not. Bank enough and you win outright.</li>
      </ol>
      <p class="note">There are no dice and no hit chance. A shot kills, and it goes off after a
      clean line has been held for long enough — so a fight is decided by who was already pointed at
      the door.</p>
      <table class="units">
        <thead><tr><th>Unit</th><th>Draw</th><th>Reach</th><th>Watch</th></tr></thead>
        <tbody>
          ${UNITS.map(
            (u) =>
              `<tr><td><b>${u.glyph} ${u.name}</b><br><span class="dim">${escapeHtml(u.blurb)}</span></td>
               <td>${(u.acquire / 20).toFixed(2)}s</td><td>${u.range}</td><td>${u.wedge * 22.5 + 11.25}&deg;</td></tr>`,
          ).join('')}
        </tbody>
      </table>
      <p class="note">A window is ${WINDOW_S} seconds. Stances: ${STANCES.map((s) => s.name).join(' · ')}.</p>
    </div>`;
}

export function aboutHtml(): string {
  return `
    <div class="panel-body">
      <p><b>Standoff</b> is a simultaneous-turn tactics game. Both sides commit orders for the same
      five seconds without seeing each other's, and the turn resolves from those orders alone.</p>
      <p>Everything you see is generated by code — the maps, the art and the sound. There are no
      images, no fonts and no assets of any kind, which is why it works offline.</p>
      <p>No cookies, no fingerprinting, no third-party fonts. Anonymous, cookie-less page-view counts
      via Cloudflare Web Analytics.</p>
      <p class="dim">Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben
      Richardson</a>. Part of <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">a
      catalogue of small games, tools and sites</a>.</p>
    </div>`;
}

/**
 * The results screen — EVERY unit's outcome, on both sides, never just yours.
 *
 * The one moment two people compare what they did is the moment after it stops, and a summary that
 * only reflects you back at yourself wastes it. So this shows each side's squad, who is left, what
 * each unit did, and how the mast time actually went — the number that decided it in most matches.
 */
export function resultsHtml(s: MatchState, you: Side0Or1, tally: [number, number], stats: SideStats[]): string {
  const won = s.winner === you;
  const drew = s.winner < 0;
  const why =
    s.ending === 'hold'
      ? 'the mast'
      : s.ending === 'wipe'
        ? 'nobody left standing'
        : s.ending === 'time'
          ? 'the clock'
          : 'a dead heat';
  return `
    <section class="screen results">
      <h2 class="verdict ${drew ? 'draw' : won ? 'win' : 'loss'}">${
        drew ? 'Dead heat' : won ? 'You win' : 'You lose'
      }</h2>
      <p class="why">Decided by ${why}, on turn ${s.turn}.</p>
      <p class="tally">Match ${tally[0]} &ndash; ${tally[1]}</p>

      <div class="sides">
        ${stats
          .map(
            (st, side) => `
          <div class="side ${side === you ? 'mine' : ''}">
            <h3><span class="dot s${side}"></span>${SEAT_NAME[side]}${side === you ? ' (you)' : ''}</h3>
            <p class="held">Held the mast <b>${(st.hold / 20).toFixed(1)}s</b> of ${(
              s.mode.holdTarget / 20
            ).toFixed(0)}s needed</p>
            <ul class="squad">
              ${st.units
                .map(
                  (u) =>
                    `<li class="${u.alive ? '' : 'dead'}"><b>${u.glyph} ${escapeHtml(u.name)}</b>
                     <span>${u.alive ? 'alive' : 'lost'} · ${u.shots} ${u.shots === 1 ? 'shot' : 'shots'} · ${
                       u.kills
                     } ${u.kills === 1 ? 'kill' : 'kills'} · walked ${u.walked.toFixed(0)}</span></li>`,
                )
                .join('')}
            </ul>
          </div>`,
          )
          .join('')}
      </div>

      <div class="results-actions">
        <button class="primary" id="again">Play again</button>
        <button class="link" id="share">Share this map</button>
        <button class="link" id="menu">Menu</button>
        <button class="link results-feedback" id="feedback">Feedback</button>
      </div>
    </section>`;
}

export type Side0Or1 = 0 | 1;

export interface SideStats {
  hold: number;
  units: Array<{ name: string; glyph: string; alive: boolean; shots: number; kills: number; walked: number }>;
}
